import React, { Fragment, useState, useEffect } from "react";
import Topbar from "../layouts/TopBar";
import { Paper } from '@mui/material';
import IncidentResources from "../resources/IncidentResources";
import LocationResources from "../resources/LocationResources";
import ServiceResources from "../resources/ServiceResources";
import { connect } from "react-redux";
import { logout } from "../redux/actions/LoggedInAction";
import Select from 'react-select';
import moment from "moment";
import { useNavigate } from 'react-router-dom';
import {ACCESS_DENIED} from '../Messages';
import toast from 'react-hot-toast';

function AddIncident({isLoggedIn, loggedInUser}){
    const navigate = useNavigate();

    const [locations, setLocations] = useState([])
    const [services, setServices] = useState([])
    const [selectedLocation, setSelectedLocation] = useState(null)
    const [selectedService, setSelectedService] = useState(null)
    const [incidentDate, setIncidentDate] = useState(moment().format('YYYY-MM-DD'))
    const [incidentTime, setIncidentTime] = useState(moment().format('HH:mm'))
    const [issues, setIssues] = useState('')

    const incidentResources = new IncidentResources()
    const locationResources = new LocationResources()
    const serviceResources = new ServiceResources()

    const loadLocations = () => {
        return locationResources.get()
    }

    const loadServices = () => {
        return serviceResources.get()
    }

    useEffect(() => {
        loadLocations().then(response => {
            if (response.error && response.error.length > 0) {
                toast.error("Failed to load locations: " + response.error)
            } else {
                const locationData = response['body'] || []
                const locationOptions = locationData.map(loc => ({
                    value: loc.id,
                    label: loc.name
                }))
                setLocations(locationOptions)
            }
        }).catch((err) => {
            toast.error("Network error loading locations: " + err)
        })

        loadServices().then(response => {
            if (response.error && response.error.length > 0) {
                // Services are optional, so just log the error but continue
                console.log("Failed to load services: " + response.error)
            } else {
                const serviceData = response['body'] || []
                const serviceOptions = serviceData.map(svc => ({
                    value: svc.id,
                    label: svc.name
                }))
                setServices(serviceOptions)
            }
        }).catch((err) => {
            console.log("Network error loading services: " + err)
        })
    }, [])

    const handleLocationChange = (selectedOption) => {
        setSelectedLocation(selectedOption)
    }

    const handleServiceChange = (selectedOption) => {
        setSelectedService(selectedOption)
    }

    const handleSubmit = () => {
        // Validation
        if (!selectedLocation) {
            toast.error("Please select a location")
            return
        }

        if (!incidentDate) {
            toast.error("Please select an incident date")
            return
        }

        if (!incidentTime) {
            toast.error("Please select an incident time")
            return
        }

        if (!issues || issues.trim() === '') {
            toast.error("Please enter incident details")
            return
        }

        // Confirmation
        if (!window.confirm("Are you sure you want to submit this incident report?")) {
            return
        }

        // Combine date and time
        const incidentDateTime = moment(`${incidentDate} ${incidentTime}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss')

        const incidentData = {
            location_id: selectedLocation.value,
            incident_date: incidentDateTime,
            issues: issues.trim(),
            entered_by_id: loggedInUser.id
        }

        // Add service_id only if selected
        if (selectedService) {
            incidentData.service_id = selectedService.value
        }

        incidentResources.post(incidentData).then(res => {
            if (res.error && res.error.length > 0) {
                toast.error("Failed to submit incident: " + res.error)
            } else {
                toast.success("Incident submitted successfully")
                // Reset form
                setSelectedLocation(null)
                setSelectedService(null)
                setIncidentDate(moment().format('YYYY-MM-DD'))
                setIncidentTime(moment().format('HH:mm'))
                setIssues('')
                // Navigate to manage incidents page
                navigate('/viewincident')
            }
        }).catch((err) => {
            toast.error("Network error: " + err)
        })
    }

    const handleCancel = () => {
        if (window.confirm("Are you sure you want to cancel? All entered data will be lost.")) {
            setSelectedLocation(null)
            setSelectedService(null)
            setIncidentDate(moment().format('YYYY-MM-DD'))
            setIncidentTime(moment().format('HH:mm'))
            setIssues('')
        }
    }


    return(
        <Fragment>
            <Topbar/>

            <div className="hero-area">
                <div className="page-banner parallax" style={{backgroundColor:"#003165"}}>
                    <div className="container">
                        <div className="page-banner-text">
                            {(isLoggedIn && loggedInUser['role.name'] === 'admin')?
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>Add Incident Report</strong></h1>
                            :
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>{ACCESS_DENIED}</strong></h1>
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div id="main-container">
                {
                    (isLoggedIn && loggedInUser['role.name'] === 'admin') ?

                <div className="content">
                    <div className="container">
                        <div className="spacer-40"></div>

                        <Paper style={{padding: '30px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px'}}>
                            <h3 style={{color: '#003165', marginBottom: '25px', fontSize: 'clamp(18px, 4.5vw, 20px)'}}>Incident Details</h3>

                            {/* Location Selection */}
                            <div style={{marginBottom: '20px'}}>
                                <label style={{color: '#003165', fontWeight: 'bold', fontSize: 'clamp(14px, 3.5vw, 15px)', marginBottom: '8px', display: 'block'}}>
                                    Location*
                                </label>
                                <Select
                                    value={selectedLocation}
                                    onChange={handleLocationChange}
                                    options={locations}
                                    placeholder="Select location..."
                                    isClearable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            fontSize: 'clamp(13px, 3.5vw, 14px)',
                                            minHeight: '42px'
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            fontSize: 'clamp(13px, 3.5vw, 14px)'
                                        })
                                    }}
                                />
                            </div>

                            {/* Service Selection (Optional) */}
                            <div style={{marginBottom: '20px'}}>
                                <label style={{color: '#003165', fontWeight: 'bold', fontSize: 'clamp(14px, 3.5vw, 15px)', marginBottom: '8px', display: 'block'}}>
                                    Service (Optional)
                                </label>
                                <Select
                                    value={selectedService}
                                    onChange={handleServiceChange}
                                    options={services}
                                    placeholder="Select service (optional)..."
                                    isClearable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            fontSize: 'clamp(13px, 3.5vw, 14px)',
                                            minHeight: '42px'
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            fontSize: 'clamp(13px, 3.5vw, 14px)'
                                        })
                                    }}
                                />
                            </div>

                            {/* Date and Time */}
                            <div style={{display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap'}}>
                                <div style={{flex: '1 1 200px', minWidth: '180px'}}>
                                    <label style={{color: '#003165', fontWeight: 'bold', fontSize: 'clamp(14px, 3.5vw, 15px)', marginBottom: '8px', display: 'block'}}>
                                        Incident Date*
                                    </label>
                                    <input
                                        type="date"
                                        value={incidentDate}
                                        onChange={(e) => setIncidentDate(e.target.value)}
                                        max={moment().format('YYYY-MM-DD')}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: 'clamp(13px, 3.5vw, 14px)',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div style={{flex: '1 1 200px', minWidth: '180px'}}>
                                    <label style={{color: '#003165', fontWeight: 'bold', fontSize: 'clamp(14px, 3.5vw, 15px)', marginBottom: '8px', display: 'block'}}>
                                        Incident Time*
                                    </label>
                                    <input
                                        type="time"
                                        value={incidentTime}
                                        onChange={(e) => setIncidentTime(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: 'clamp(13px, 3.5vw, 14px)',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Incident Details */}
                            <div style={{marginBottom: '25px'}}>
                                <label style={{color: '#003165', fontWeight: 'bold', fontSize: 'clamp(14px, 3.5vw, 15px)', marginBottom: '8px', display: 'block'}}>
                                    Incident Details*
                                </label>
                                <textarea
                                    value={issues}
                                    onChange={(e) => setIssues(e.target.value)}
                                    placeholder="Describe the incident in detail..."
                                    rows={8}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: 'clamp(13px, 3.5vw, 14px)',
                                        boxSizing: 'border-box',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            {/* Action Buttons */}
                            <div style={{display: 'flex', gap: '15px', justifyContent: 'flex-end', flexWrap: 'wrap'}}>
                                <button
                                    onClick={handleCancel}
                                    style={{
                                        backgroundColor: '#6c757d',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '12px 30px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: 'clamp(14px, 3.5vw, 15px)'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    style={{
                                        backgroundColor: '#35ab7c',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '12px 30px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: 'clamp(14px, 3.5vw, 15px)'
                                    }}
                                >
                                    Submit Incident
                                </button>
                            </div>
                        </Paper>

                        <div className="spacer-50"></div>
                    </div>
                </div> : <></>
                }
            </div>

        </Fragment>
    )
}

const mapStateToProps = (state) => {
    return{
       isLoggedIn: state.authReducer.isLoggedIn,
       loggedInUser: state.authReducer.user
    };
    
};

const mapDispatchToProps = {
    logout
};

export default connect(mapStateToProps, mapDispatchToProps)(AddIncident);